<template>
    <v-card>
        <v-card-text>
          <v-form @submit.prevent="onSubmit">
            <v-row>
              <v-col>
                <div v-if="action === 'create'">
                  <vf-autocomplete
                    :label="$tc('customer', 2)"
                    v-model="createOptions.customers"
                    :items="customers"
                    item-text="name"
                    item-value="id"
                    return-object />
                </div>
                <div v-else>
                  <v-select
                    :label="$tc('customer', 1)"
                    v-model="localBanner.customer_id"
                    :items="customers"
                    item-text="name"
                    item-value="id" />
                </div>
                <v-select
                  :label="$tc('type', 1)"
                  v-model="localBanner.type"
                  :items="bannerTypes"
                  item-text="displayName"
                  item-value="name"
                  :error-messages="typeErrors" />
                <v-select
                  :label="$tc('style', 1)"
                  v-model="localBanner.style"
                  :items="bannerStyles"
                  item-text="displayName"
                  item-value="value"
                  :error-messages="styleErrors" />
                <rich-text-editor v-model="localBanner.body" class="my-2"/>
                <v-row>
                  <v-col>
                    <date-picker
                      v-model="expirationDate"
                      :label="$tc('expiration_date')"
                      prepend-icon="mdi-calendar"
                      :timezone="customerTimezone"
                      :reset-on-timezone-change="action === 'create'"/>
                  </v-col>
                  <v-col>
                    <time-picker
                      v-model="expirationTime"
                      :label="$t('expiration_time_placeholder')"
                      prepend-icon="mdi-clock"
                      :reset-on-timezone-change="action === 'create'"/>
                  </v-col>
                </v-row>

                <v-switch
                  v-model="localBanner.visible"
                  :label="$tc('visible')"/>
              </v-col>
            </v-row>
            <v-btn v-if="!localBanner.id"
                   type="submit"
                   color="primary"
                   @click="$gtag.event('create', {event_category: 'Banner'})"
                   :loading="isSaving">
              <v-icon class="ml-n2">mdi-content-save</v-icon>
              {{ $t('create') }}
            </v-btn>
            <v-btn v-else
                   type="submit"
                   color="primary"
                   @click="$gtag.event('update', {event_category: 'Banner'})"
                   :loading="isSaving">
              <v-icon class="ml-n2">mdi-content-save</v-icon>
              {{ $t('update') }}
            </v-btn>
          </v-form>
        </v-card-text>
    </v-card>
</template>

<script>

import RichTextEditor from '@/components/shared/RichTextEditor'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import { required } from 'vuelidate/lib/validators'

import Moment from 'moment-timezone'
import { cloneDeep, map, isEmpty } from 'lodash'
import DatePicker from '@/components/shared/DatePicker'
import TimePicker from '@/components/shared/TimePicker'
import VfAutocomplete from '@/components/shared/VfAutocomplete.vue'

export default {
  name: 'BannerForm',
  components: { VfAutocomplete, DatePicker, TimePicker, RichTextEditor },
  mixins: [CurrentUserMixin, ApiUtilitiesMixin, ValidationMixin],
  props: {
    banner: {
      type: Object
    }
  },
  validations: {
    localBanner: {
      customer_id: {
        /**
         * If the action is 'update', then require the customer_id to be set
         */
        requiredIfUpdating: function (value) {
          if (this.action === 'update') {
            return value != null
          }
          return true
        }
      },
      style: {
        required
      },
      body: {
        required
      },
      type: {
        required
      }
    },
    expirationDate: {
      requiredIfTimeEntered: function (value) {
        return !(isEmpty(this.expirationDate) && !isEmpty(this.expirationTime))
      }
    },
    createOptions: {
      customers: {
        /**
         * If the action is 'create', then require customers to be non-empty
         */
        requiredIfCreating: function (value) {
          return !(this.action === 'create' && isEmpty(value))
        }
      }
    }
  },
  data () {
    return {
      isSaving: false,
      // If editing an existing record, create an edit clone to avoid modifying elsewhere.
      // cloneDeep seems to have issues with the output of $orm('Model').createRecord, but we don't need to clone
      // a new record, now do we?
      localBanner: cloneDeep(this.banner) || this.$orm('Banner').createRecord({ visible: 1 }),
      bannerTypes: [],
      bannerStyles: [
        { value: 'danger', displayName: 'Danger (red)' },
        { value: 'warning', displayName: 'Warning (yellow)' },
        { value: 'success', displayName: 'Success (green)' },
        { value: 'info', displayName: 'Info (blue)' }
      ],
      createOptions: {
        customers: []
      },
      expirationDate: null,
      expirationTime: null
    }
  },
  computed: {
    /**
     * Get the current action (so we can handle update/create differently)
     * @return {string}
     */
    action () {
      return this.localBanner.id == null ? 'create' : 'update'
    },
    timeZoneLabel () {
      return Moment.tz(this.customerContext.timezone()).zoneAbbr()
    },
    /**
     * Compute a unified expiration time from the separate expiration date/time pickers.
     * Return null if no value can be computed. Return midnight of the selected date if
     * no time was selected.
     * @return string|null
     */
    expirationDateTime () {
      if (this.expirationDate) {
        const returnValue = Moment.tz(this.expirationDate, this.customerTimezone).startOf('day')
        if (this.expirationTime) {
          const parts = this.expirationTime.split(':')
          returnValue.hours(parts[0])
          returnValue.minutes(parts[1])
          returnValue.seconds(parts[2])
        }
        return returnValue.format()
      }
      return null
    },
    styleErrors () {
      return this.validationErrors([{
        prop: this.$v.localBanner.style,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    bodyErrors () {
      return this.validationErrors([{
        prop: this.$v.localBanner.body,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    typeErrors () {
      return this.validationErrors([{
        prop: this.$v.localBanner.type,
        rule: 'required',
        message: this.$t('required')
      }])
    }
  },
  methods: {
    /**
     * Submit the form.
     */
    onSubmit (e) {
      this.$v.$touch()
      if (this.$v.$invalid) return false

      this.localBanner.expiration = this.expirationDateTime

      if (this.action === 'update') {
        this.updateBanner(this.localBanner)
      } else {
        this.createBanners(this.localBanner, map(this.createOptions.customers, c => c.id))
      }
    },
    /**
     * Update a single banner in the normal fashion
     * @param banner
     */
    updateBanner (banner) {
      this.isSaving = true
      this.$orm('Banner').createOrUpdate(banner)
        .then(banner => {
          this.$router.push('/banners', () => {
            this.$store.dispatch(
              'globalMessagingModule/showSuccessFlashBanner',
              this.$i18n.t('banner_save_success')
            )
          })
        })
        .catch(error => {
          this.renderRecordSaveErrorFlashBanner(error)
          throw error
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    /**
     * Create multiple banners, one for each customer selected with createOptions.customers
     * Uses Promise.all, so if any save fails, the process will be stopped and an error displayed.
     *
     * @param {Object} banner The banner to create.
     * @param {Array } customerIds An array of customer ids to assign the banner to.
     */
    createBanners (banner, customerIds) {
      this.isSaving = true
      const promises = map(customerIds, customerId => {
        // Need to manually clone the banner each time we create a new one, as lodash cloning has issues.
        // (it leaves the _set function undefined, which results in the save failing.)
        const bannerClone = this.$orm('Banner').createRecord({
          body: banner.body,
          style: banner.style,
          type: banner.type,
          customer_id: customerId,
          visible: banner.visible,
          expiration: banner.expiration
        })
        return this.$orm('Banner').createOrUpdate(bannerClone)
      })

      Promise.all(promises)
        .then(values => {
          this.$router.push('/banners', () => {
            this.$store.dispatch(
              'globalMessagingModule/showSuccessFlashBanner',
              this.$i18n.t('banner_create_success_multiple', { num: values.length })
            )
          })
        })
        .catch(error => {
          this.$store.dispatch(
            'globalMessagingModule/showErrorFlashBanner',
            this.$i18n.t('banner_create_failure_multiple')
          )
          this.renderRecordSaveErrorFlashBanner(error)
          throw error
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    /**
     * Populate the separate values used in the expiration date and time pickers from the model's
     * expiration value, if it is set.
     */
    populateExpirationValues () {
      if (this.localBanner.expiration) {
        let expiration
        const value = this.localBanner.expiration
        if (typeof value === 'object' && value !== null && value.date && value.timezone) {
          // VFCarbon serialized format: {date: "2026-04-03 04:00:00.000000", timezone: "UTC", timezone_type: 3}
          // Parse the date in its original timezone, then convert to customer timezone
          expiration = Moment.tz(value.date, value.timezone).tz(this.customerTimezone)
        } else {
          expiration = Moment.tz(value, this.customerTimezone)
        }
        this.expirationDate = expiration.clone().startOf('day').format()
        this.expirationTime = expiration.format('HH:mm:ss')
      }
    },
    customerContextChangeHandler () {
      // This will revert the expiration pickers to the last persisted expiration time.
      // This seems like the best course of action because it's the least disorienting
      // if the customer changes.
      this.populateExpirationValues()
    }
  },
  created () {
    // Limit the type selection to the current type (defaulted to the "General type"
    // for a new banner). Each type has specific parameters and we don't yet support
    // the UI necessary to choose all the type specific stuff.
    if (!this.localBanner.type) {
      this.localBanner.type = 'general'
    }
    this.bannerTypes = [
      {
        name: this.localBanner.type,
        displayName: this.$t(this.localBanner.type)
      }
    ]
    this.populateExpirationValues()
  }
}
</script>

<style>
  .pro-tip {
    color: darkgrey;
    font-style: italic;
  }
</style>
